import { configureStore, EnhancedStore } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import auth, { setupAuth } from '@src/features/customers';
import app, { setPublicKey, loadAppInfo } from '@src/stores/app';
import { readBuyerToken } from '@src/common/buyerToken';
import cart, { setupCart } from '@src/features/cart/store/cart';
import product from '@src/stores/product';
import productUpsell from '@src/stores/productUpsell';
import multiProductBooking from '@src/stores/multiProductBooking';
import order from '@src/features/orders/store/order';
import sale from '@src/stores/sale';
import saleChange from '@src/stores/saleChange';
import i18n from '@src/stores/i18nMessages';
import userPreferences, { setupLocale, setWidgetUrl } from '@src/stores/userPreferences';
import globalParameters, { setGlobalParameter } from '@src/stores/globalParameters';
import checkout from '@src/stores/checkout';
import directLink from '@src/stores/directLink';
import ui, { setScreenWidth } from '@src/stores/ui';
import payment from '@src/features/payments/store/payment';
import {
  AppsApi,
  BuyersApi,
  ProductsApi,
  CartsApi,
  OrdersApi,
  SalesApi,
  SaleItemsApi,
  FormInputsApi,
  PaymentOptionsApi,
  PaymentMethodsApi,
  VouchersApi,
  SchedulingsApi,
  SeatMapsApi,
  DirectLinksApi,
  TransactionsApi,
  Configuration,
  ErrorResponse,
  ProductPackagesApi,
  TariffTypeVerificationsApi,
  SaleChangesApi,
  TariffGroupAttributesApi,
  UpsellOffersApi,
  CartItemsApi,
} from '@src/core/buyerApiClient';
import monitoringMiddleware from './monitoringMiddleware';
import { getStoredUtms, appendQueryParams, getSessionId } from '@src/common';
import { withCurrentLocalePrefix } from '@src/common/localePath';
import { navigateTo } from '@src/common/utils/url';
import { PaymentErrorsEnum } from '@src/domains/payments';
import { DEFAULT_CURRENCY } from '@src/common/currency';

export const store = configureStore({
  reducer: {
    auth,
    app,
    cart,
    product,
    productUpsell,
    multiProductBooking,
    order,
    sale,
    saleChange,
    globalParameters,
    userPreferences,
    i18n,
    checkout,
    directLink,
    payment,
    ui,
  },
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(monitoringMiddleware),
});

window.addEventListener('resize', () => {
  store.dispatch(setScreenWidth(window.innerWidth));
});

export type RootState = ReturnType<typeof store.getState>;
export type RootStore = EnhancedStore<RootState>;
export type AppDispatch = typeof store.dispatch;

declare const __BUYER_API_TARGET__: string;

export const getClient = (state: RootState) => {
  const cfg = new Configuration({
    basePath: __BUYER_API_TARGET__.replace(/\/$/, ''),
    apiKey: state.app.publicApiKey,
    baseOptions: {
      headers: {
        'X-Client-Language': state.userPreferences.originalLocale,
        'X-Currency': state.globalParameters.values['currency'] || 'BRL',
        'Cache-Control': 'no-store',
      },
      timeout: 45000,
    },
  });

  return {
    apps: new AppsApi(cfg),
    buyers: new BuyersApi(cfg),
    products: new ProductsApi(cfg),
    carts: new CartsApi(cfg),
    orders: new OrdersApi(cfg),
    sales: new SalesApi(cfg),
    saleItems: new SaleItemsApi(cfg),
    formInputs: new FormInputsApi(cfg),
    paymentOptions: new PaymentOptionsApi(cfg),
    paymentMethods: new PaymentMethodsApi(cfg),
    vouchers: new VouchersApi(cfg),
    schedulings: new SchedulingsApi(cfg),
    seatMaps: new SeatMapsApi(cfg),
    directLinks: new DirectLinksApi(cfg),
    transactions: new TransactionsApi(cfg),
    productPackage: new ProductPackagesApi(cfg),
    tariffTypeVerifications: new TariffTypeVerificationsApi(cfg),
    saleChanges: new SaleChangesApi(cfg),
    tariffGroupAttributes: new TariffGroupAttributesApi(cfg),
    upsellOffers: new UpsellOffersApi(cfg),
    cartItems: new CartItemsApi(cfg),
  };
};

export interface PayloadMap<T> {
  id: string;
  value: T;
}

export const bearerAuth = (token: string) => ({
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

// Cart operations are checked against the cart's owner. A cart gains one at checkout and keeps the
// same id, so without the token the owner's own next request comes back 403. Reads the same source
// setupAuth does, because the cart's first request happens before setupAuth has hydrated the store.
export const cartAuth = (state: RootState) => {
  const token = readBuyerToken(state.app.publicApiKey, state.auth.buyerToken);

  return token ? bearerAuth(token) : {};
};

export type ValidationError = {
  [key: string]: string[];
};

export const validationErrors = (err: unknown) => {
  if ((err as AxiosError).response?.status == 422) {
    const errorResponse = (err as AxiosError).response?.data as ErrorResponse;

    return errorResponse.errors?.reduce<ValidationError>((carry, err) => {
      return {
        ...carry,
        [err.path]: [...(carry[err.path] ?? []), getErrorMessage(err.type)],
      };
    }, {});
  }

  return undefined;
};

// TODO add translation in the future
export const getErrorMessage = (errType: string) => {
  switch (errType) {
    case 'required':
    case 'requiredValue':
      return 'O campo é obrigatório';
    case 'emailInputFormat':
      return 'Email inválido';
    case 'phoneInputFormat':
      return 'Telefone inválido';
    case 'cpfInputFormat':
      return 'CPF inválido';
    case 'numericInputFormat':
      return 'Use apenas números';
    case 'mustHaveDateInputFormat':
      return 'Data inválida';
    case 'invalidValue':
      return 'Valor inválido';
    case 'invalidOption':
      return 'Opção inválida';
    case 'unique':
      return 'Valor já está em uso';
    case 'emailMustBeUnique':
      return 'Email já está em uso';
    case 'emailMustExist':
      return 'Não conseguimos confirmar este e-mail. Confira se ele foi digitado corretamente ou use outro endereço.';
    case 'emailDomainMustBeValid':
      return 'Email inválido';
    case 'phoneMustBeValid':
      return 'Telefone inválido';
    case 'passwordMustBeEqual':
      return 'As senhas não coincidem';
    case 'min':
      return 'Mínimo de 8 caracteres';
    case 'passwordStrength':
      return 'A senha deve conter no mínimo 8 caracteres, com ao menos uma letra maiúscula e uma letra minúscula';
    case 'tariffQuantityMustRespectProductLimit':
      return 'A quantidade selecionada passa do limite por compra deste produto';
    case 'cartItemLimitMustBeRespected':
      return 'O carrinho atingiu o número máximo de itens';
    case PaymentErrorsEnum.CARD_NOT_VALID_FOR_THIS_OPERATION:
      return 'O cartão de crédito informado não é qualificado para a aquisição das tarifas selecionadas. Utilize outro cartão.';
    default:
      return errType;
  }
};

// The buyer API's request validator has no custom error handler, so a schema violation answers 400 in
// plain text instead of the 422 ErrorResponse shape the rest of the API uses.
export const contractViolationErrors = (err: unknown): ValidationError | undefined => {
  if ((err as AxiosError).response?.status !== 400) {
    return undefined;
  }

  return { quantity: ['Quantidade inválida'] };
};

export const validationErrorsWithoutI18n = (err: unknown) => {
  if ((err as AxiosError).response?.status == 422) {
    const errorResponse = (err as AxiosError).response?.data as ErrorResponse;

    return errorResponse.errors?.reduce<ValidationError>(
      (carry, err) => ({
        ...carry,
        [err.path]: [...(carry[err.path] ?? []), err.type],
      }),
      {},
    );
  }

  return undefined;
};

export const initializeStore =
  (key: string, language: string, url: string, currency: string = DEFAULT_CURRENCY) =>
  async (dispatch: AppDispatch) => {
    dispatch(setGlobalParameter('sessionId', getSessionId()));
    dispatch(setWidgetUrl(url));
    dispatch(setupLocale(language));
    dispatch(setGlobalParameter('currency', currency));
    dispatch(setPublicKey(key));
    setTimeout(() => {
      dispatch(loadAppInfo());
    }, 200);
    dispatch(setupAuth(false));
    dispatch(setupCart());
  };

export const changeLocation = (url: string) => () => {
  const storedUtms = getStoredUtms();
  const localizedUrl = withCurrentLocalePrefix(url);

  navigateTo(appendQueryParams(localizedUrl, storedUtms) ?? localizedUrl);
};

export const sendFbPixelEvent =
  (pixelId: string, accessTokenPath: string, eventData: any) => async (_: AppDispatch, getState: () => RootState) => {
    const {
      app: { publicApiKey },
    } = getState();
    const url = __BUYER_API_TARGET__.replace(/\/$/, '') + '/integrations/facebookPixel/events';

    if (publicApiKey) {
      await axios.post(
        url,
        { pixelId, accessTokenPath, eventData },
        {
          headers: { 'X-App-Token': publicApiKey },
        },
      );
    }
  };
